import {isPlatformBrowser} from '@angular/common';
import {PushNotificationsService} from './push/push-notifications.service';
import {Component, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router, RoutesRecognized} from '@angular/router';
import {Subscription} from 'rxjs';
import {MenuController} from '@ionic/angular';
import {App} from '@capacitor/app';
import {Browser} from '@capacitor/browser';
import {Capacitor} from '@capacitor/core';
import type {PluginListenerHandle} from '@capacitor/core';
import {AuthService} from './auth/auth.service';
import {AppearanceService} from './appearance/appearance.service';
import {AuthSessionResumeService} from './auth/auth-session-resume.service';
import type {AuthSession} from './auth/auth.models';
import {isAllowedNativeQrUrl, isNativeAuthCallback, isStagingEnvironment} from './auth/native-auth-config';
import {safeAuthReturnUrl} from './auth/auth-return-url';
import {AppLanguageService} from './shared/i18n/app-language.service';
import {buildPlatformAwareRoutePathname, stripLocalizedRouteLanguageFromPathname} from './shared/i18n/app-language';
import {extractQrToken} from './shared/qr-scanner/extract-qr-token';
import {RegionalPreferencesService} from './shared/settings/regional-preferences.service';
import {AppUpdateService} from './shared/app-update/app-update.service';
import {ApplicationIdentityService} from './shared/config/application-identity.service';
import {PublicSeoMetadataService} from './shared/seo/public-seo-metadata.service';
import {
  restoreMobileStatusBarOnResume,
  MobileAppStatePort,
} from './shared/mobile-chrome/mobile-status-bar';
import {AnalyticsService} from './shared/analytics/analytics.service';
import {ExternalBrowserService} from './shared/external-browser.service';
import {UniversalScannerBackService} from './shared/scanning/universal-scanner-back.service';
import {WebAnalyticsConsentService} from './shared/web-analytics/web-analytics-consent.service';
import {ApiCompatibilityService, ApiCompatibilityState} from './shared/api/api-compatibility.service';
import {SmModalStackService} from './shared/components/sm-confirmation-dialog/sm-modal-stack.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  readonly isBrowser = isPlatformBrowser(this.platformId);
  readonly isStaging = isStagingEnvironment();
  analyticsConsentBusy = false;

  private appUrlOpenListener?: PluginListenerHandle;
  private backButtonListener?: PluginListenerHandle;
  private mobileChromeAppStateListener?: PluginListenerHandle;
  private readonly routerEventsSubscription: Subscription;
  private pendingNavigationUrl?: string;
  private destroyed = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly authService: AuthService,
    private readonly authSessionResumeService: AuthSessionResumeService,
    private readonly appLanguageService: AppLanguageService,
    private readonly ngZone: NgZone,
    private readonly modalStack: SmModalStackService,
    private readonly router: Router,
    private readonly regionalPreferencesService: RegionalPreferencesService,
    private readonly appUpdateService: AppUpdateService,
    private readonly applicationIdentityService: ApplicationIdentityService,
    private readonly publicSeoMetadataService: PublicSeoMetadataService,
    private readonly analyticsService: AnalyticsService,
    private readonly externalBrowserService: ExternalBrowserService,
    readonly webAnalyticsConsentService: WebAnalyticsConsentService,
    private readonly appearanceService: AppearanceService,
    private readonly universalScannerBack: UniversalScannerBackService,
    private readonly menuController: MenuController,
    readonly apiCompatibility: ApiCompatibilityService,
    private readonly pushNotifications?: PushNotificationsService,
  ) {
    this.routerEventsSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) this.pendingNavigationUrl = event.url;
      else if (event instanceof RoutesRecognized) this.pendingNavigationUrl = event.urlAfterRedirects;
      else if (event instanceof NavigationEnd || event instanceof NavigationCancel
        || event instanceof NavigationError || event instanceof NavigationSkipped) this.pendingNavigationUrl = undefined;
    });
  }

  get showAnalyticsConsentDialog(): boolean {
    return !this.compatibilityBlocked && (this.analyticsService.requiresConsentDecision
      || (this.webAnalyticsConsentService.settingsOpen && !this.suppressWebAnalyticsConsentDialog));
  }

  get compatibilityBlocked(): boolean {
    if (!this.isBrowser || !this.apiCompatibility.active() || this.apiCompatibility.state() === 'compatible') return false;
    const routePath = this.normalizeRoutePathname(new URL(
      this.pendingNavigationUrl ?? this.currentRouteUrl, 'https://invemory.local',
    ).pathname);
    const authRoute = /^\/(?:login|register|forgot-password|reset-password|verify-email|logout)(?:\/|$)/.test(routePath);
    if (Capacitor.getPlatform() !== 'web') return !authRoute;
    return !authRoute && !/^\/(?:$|checkout(?:\/|$)|whats-new(?:\/|$)|about(?:\/|$)|terms(?:\/|$)|privacy(?:\/|$)|account-deletion(?:\/|$)|guides(?:\/|$)|items(?:\/|$)|ssr-demo(?:\/|$))/.test(routePath);
  }

  get compatibilityState(): ApiCompatibilityState | undefined {
    return this.compatibilityBlocked || this.apiCompatibility.recoveryRequested()
      ? this.apiCompatibility.state() : undefined;
  }

  get analyticsConsentTranslationPrefix(): string {
    return this.webAnalyticsConsentService.settingsOpen
      ? 'webAnalyticsConsentDialog'
      : 'analyticsConsentDialog';
  }

  async ngOnInit(): Promise<void> {
    this.destroyed = false;
    this.appearanceService.initialize();
    this.regionalPreferencesService.initialize(this.authService);
    void this.applicationIdentityService.initialize();
    this.publicSeoMetadataService.initialize();
    void this.analyticsService.initialize();

    if (!this.isBrowser) {
      return;
    }

    void this.apiCompatibility.initialize();
    void this.configureMobileChrome();
    void this.configureMobileChromeOnResume();
    void this.authSessionResumeService.initialize();
    void this.pushNotifications?.initialize().catch(() => undefined);
    void this.appUpdateService.initialize();
    await this.configureNativeBackNavigation();

    this.appUrlOpenListener = await App.addListener('appUrlOpen', ({url}) => {
      void this.handleAppUrlOpen(url);
    });

    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      await this.handleAppUrlOpen(launchUrl.url);
    }
  }

  async ngOnDestroy(): Promise<void> {
    this.destroyed = true;
    this.routerEventsSubscription.unsubscribe();
    if (!this.isBrowser) {
      return;
    }

    await this.appUrlOpenListener?.remove();
    await this.backButtonListener?.remove();
    await this.mobileChromeAppStateListener?.remove();
    await this.authSessionResumeService.destroy();
    await this.appUpdateService.destroy();
    await this.apiCompatibility.destroy();

  }

  async chooseAnalyticsConsent(granted: boolean): Promise<void> {
    if (this.analyticsConsentBusy) {
      return;
    }

    this.analyticsConsentBusy = true;
    try {
      if (this.webAnalyticsConsentService.settingsOpen) {
        this.webAnalyticsConsentService.choose(granted);
      } else {
        await this.analyticsService.completeConsentDecision(granted);
      }
    } finally {
      this.analyticsConsentBusy = false;
    }
  }

  async openAnalyticsPrivacyPolicy(): Promise<void> {
    const privacyPath = buildPlatformAwareRoutePathname('/privacy', this.appLanguageService.languageCode);
    await this.externalBrowserService.open(privacyPath);
  }

  private get suppressWebAnalyticsConsentDialog(): boolean {
    try {
      const currentUrl = new URL(this.currentRouteUrl, 'https://invemory.local');
      const routePath = this.normalizeRoutePathname(currentUrl.pathname);
      if (routePath === '/privacy') {
        return true;
      }

      if (routePath === '/login') {
        return currentUrl.searchParams.get('nativeGoogleAuth') === '1';
      }

      if (routePath !== '/logout') {
        return false;
      }

      const nextPath = currentUrl.searchParams.get('next');
      if (!nextPath) {
        return false;
      }

      const nextUrl = new URL(nextPath, currentUrl.origin);
      return nextUrl.origin === currentUrl.origin
        && this.normalizeRoutePathname(nextUrl.pathname) === '/login'
        && nextUrl.searchParams.get('nativeGoogleAuth') === '1';
    } catch {
      return false;
    }
  }

  private normalizeRoutePathname(pathname: string): string {
    const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
    return stripLocalizedRouteLanguageFromPathname(normalizedPathname);
  }

  private get currentRouteUrl(): string {
    if (this.router.navigated && this.router.url) return this.router.url;
    if (this.isBrowser && typeof window !== 'undefined') {
      return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }

    return this.router.url || '/';
  }

  private async handleAppUrlOpen(url: string): Promise<void> {
    const normalizedUrl = this.normalizeAppLaunchUrl(url);
    const session = this.tryExtractAuthSession(normalizedUrl);
    if (session) {
      await this.ngZone.run(async () => {
        const homePath = this.nativeAuthReturnPath();
        await this.authService.setSession(session);
        await this.analyticsService.login('google');

        try {
          await Browser.close();
        } catch {
          // Ignore browser close failures when the browser is already gone.
        }

        if (this.shouldUseHardReload() && typeof window.location.replace === 'function') {
          window.location.replace(homePath);
          return;
        }

        await this.router.navigateByUrl(homePath, {replaceUrl: true});
      });
      return;
    }

    const qrToken = this.tryExtractQrToken(normalizedUrl);
    if (!qrToken) {
      return;
    }

    const homePath = this.buildQrSearchPath(qrToken);
    await this.ngZone.run(async () => this.router.navigateByUrl(homePath, {replaceUrl: true}));
  }

  private nativeAuthReturnPath(): string {
    const current = new URL(this.currentRouteUrl, 'https://invemory.local');
    return safeAuthReturnUrl(current.searchParams.get('returnUrl'))
      ?? buildPlatformAwareRoutePathname('/home', this.appLanguageService.languageCode);
  }

  private tryExtractAuthSession(url: string): AuthSession | null {
    try {
      const parsedUrl = new URL(url);
      if (!isNativeAuthCallback(parsedUrl)) {
        return null;
      }

      const hashParams = new URLSearchParams(parsedUrl.hash.replace(/^#/, ''));
      const rawSession = hashParams.get('session');
      if (!rawSession) {
        return null;
      }

      return JSON.parse(decodeURIComponent(rawSession)) as AuthSession;
    } catch {
      return null;
    }
  }

  private tryExtractQrToken(url: string): string | null {
    if (!isAllowedNativeQrUrl(url)) return null;
    const token = extractQrToken(url);
    return token && token !== url.trim() ? token : null;
  }

  private normalizeAppLaunchUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed.toLowerCase().startsWith('intent://')) {
      return trimmed;
    }

    const intentMarker = '#Intent;';
    const intentMarkerIndex = trimmed.indexOf(intentMarker);
    if (intentMarkerIndex < 0 || !trimmed.toLowerCase().endsWith(';end')) {
      return trimmed;
    }

    const targetPathAndQuery = trimmed.slice('intent://'.length, intentMarkerIndex);
    const intentParams = trimmed.slice(intentMarkerIndex + intentMarker.length, -';end'.length);
    const fallbackUrl = this.readIntentStringExtra(intentParams, 'browser_fallback_url');
    if (fallbackUrl) {
      return fallbackUrl;
    }

    const scheme = this.readIntentParam(intentParams, 'scheme') || 'https';
    return `${scheme}://${targetPathAndQuery}`;
  }

  private readIntentParam(intentParams: string, key: string): string | null {
    const pattern = new RegExp(`(?:^|;)${key}=([^;]+)`, 'i');
    const match = intentParams.match(pattern);
    if (!match?.[1]) {
      return null;
    }

    return this.decodeIntentValue(match[1]);
  }

  private readIntentStringExtra(intentParams: string, key: string): string | null {
    const pattern = new RegExp(`(?:^|;)S\\.${key}=([^;]+)`, 'i');
    const match = intentParams.match(pattern);
    if (!match?.[1]) {
      return null;
    }

    return this.decodeIntentValue(match[1]);
  }

  private decodeIntentValue(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private buildQrSearchPath(token: string): string {
    const homePath = buildPlatformAwareRoutePathname('/home', this.appLanguageService.languageCode);
    return `${homePath}?qr=${encodeURIComponent(token)}`;
  }

  private async configureNativeBackNavigation(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    this.backButtonListener = await App.addListener('backButton', ({canGoBack}) => {
      void this.ngZone.run(() => this.handleNativeBackNavigation(canGoBack));
    });
  }

  private async handleNativeBackNavigation(canGoBack: boolean): Promise<void> {
    if (this.universalScannerBack.closeActiveScanner()) return;
    if (this.modalStack.closeTopOverlay()) return;
    const modal = this.modalStack.top()
      ?? document.querySelector<HTMLDialogElement>('dialog[data-sm-modal][open]');
    if (modal) {
      modal.dispatchEvent(new Event('cancel', {cancelable: true}));
      return;
    }
    if (await this.menuController.isOpen('main-menu')) {
      await this.menuController.close('main-menu');
      return;
    }
    if (canGoBack) {
      window.history.back();
      return;
    }
    void App.exitApp();
  }

  private async configureMobileChrome(): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform !== 'android' && platform !== 'ios') {
      return;
    }

    try {
      await this.appearanceService.refreshNativeChrome();
    } catch {
      // Native shell is unavailable on web and can fail during early startup.
    }
  }

  private async configureMobileChromeOnResume(app: MobileAppStatePort = App): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') {
      return;
    }

    try {
      const listener = await restoreMobileStatusBarOnResume(app, async () => {
        await this.appearanceService.refreshNativeChrome();
        void this.appearanceService.reload();
      });

      if (this.destroyed) {
        await listener.remove();
        return;
      }

      this.mobileChromeAppStateListener = listener;
    } catch {
      // Status-bar restoration is best-effort and must not block app startup.
    }
  }

  private shouldUseHardReload(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.location.port !== '9876';
  }
}
